(function () {

  // ===== Configuration =====
  const FEATURED_PHOTO_REVIEW_COUNT = 4;   // used only in "Newest (photo-first)" mode
  const CAROUSEL_THRESHOLD_DESKTOP = 6;
  const CAROUSEL_THRESHOLD_MOBILE = 3;

  // ----- Stars -----
  let mwStarUid = 0;

  function starPath() {
    return "M12 17.27l-5.18 3.05 1.64-5.81L3 9.24l6.06-.52L12 3l2.94 5.72 6.06.52-5.46 5.27 1.64 5.81z";
  }
  function starSVGFilled() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${starPath()}"></path></svg>`;
  }
  function starSVGOutline() {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="#000" stroke-width="1.6" d="${starPath()}"></path></svg>`;
  }
  function starSVGHalf() {
    const id = `mwStarGrad_${++mwStarUid}`;
    return `<svg viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id="${id}" x1="0" y1="0" x2="1" y2="0">
          <stop offset="50%" stop-color="#000"></stop>
          <stop offset="50%" stop-color="transparent"></stop>
        </linearGradient>
      </defs>
      <path d="${starPath()}" fill="url(#${id})"></path>
      <path d="${starPath()}" fill="none" stroke="#000" stroke-width="1.6"></path>
    </svg>`;
  }

  function starsHTML(rating) {
    const r = Math.max(0, Math.min(5, Number(rating) || 0));
    let full = Math.floor(r);
    const frac = r - full;

    let half = 0;
    if (frac >= 0.75) full += 1;
    else if (frac >= 0.25) half = 1;

    full = Math.min(full, 5);
    const empty = Math.max(0, 5 - full - half);

    let out = `<span class="mw-stars" aria-label="${r.toFixed(2)} out of 5">`;
    for (let i = 0; i < full; i++) out += starSVGFilled();
    if (half) out += starSVGHalf();
    for (let i = 0; i < empty; i++) out += starSVGOutline();
    out += `</span>`;
    return out;
  }

  function escapeHTML(str) {
    return String(str ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function fmtDate(v) {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(d);
  }

  async function fetchText(url) {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error("HTTP " + res.status);
    return await res.text();
  }

  function parseCSV(csvText) {
    const rows = [];
    let row = [];
    let field = "";
    let i = 0;
    let inQuotes = false;

    while (i < csvText.length) {
      const c = csvText[i];
      if (inQuotes) {
        if (c === '"') {
          if (csvText[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQuotes = false; i++; continue;
        }
        field += c; i++; continue;
      } else {
        if (c === '"') { inQuotes = true; i++; continue; }
        if (c === ",") { row.push(field); field = ""; i++; continue; }
        if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
        if (c === "\r") { i++; continue; }
        field += c; i++; continue;
      }
    }
    row.push(field);
    rows.push(row);
    return rows;
  }

  // ---------------- Image handling (Drive-only hardening) ----------------

  function isDriveLikeUrl(u) {
    const s = String(u || "");
    return /drive\.google\.com|googleusercontent\.com/i.test(s);
  }

  function extractDriveId(url) {
    const s = String(url || "").trim();
    if (!s) return "";

    let m = s.match(/drive\.google\.com\/file\/d\/([^\/]+)\//i);
    if (m && m[1]) return m[1];

    m = s.match(/drive\.google\.com\/uc\?.*?[?&]id=([^&]+)/i);
    if (m && m[1]) return m[1];

    m = s.match(/drive\.google\.com\/thumbnail\?.*?[?&]id=([^&]+)/i);
    if (m && m[1]) return m[1];

    return "";
  }

  function normalizePhotoUrl(u) {
    u = String(u || "").trim();
    if (!u) return "";

    const id = extractDriveId(u) || (u.match(/^https?:\/\/drive\.google\.com\/file\/d\/([^\/]+)\//i)?.[1] ?? "");
    if (id) {
      return `https://drive.google.com/uc?export=view&id=${id}&filename=review.jpg`;
    }
    return u;
  }

  function driveFallbackUrls(primaryUrl) {
    const id = extractDriveId(primaryUrl);
    if (!id) return [];
    return [
      `https://drive.google.com/thumbnail?id=${id}&sz=w1200`,
      `https://drive.google.com/uc?export=download&id=${id}&filename=review.jpg`,
      `https://lh3.googleusercontent.com/d/${id}`
    ];
  }

  function applyDriveImgAttrs(img) {
    img.referrerPolicy = "no-referrer";
    img.crossOrigin = "anonymous";
  }

  function installDriveFallback(img, originalSrc) {
    if (!isDriveLikeUrl(originalSrc)) return;
    const fallbacks = driveFallbackUrls(originalSrc);
    if (!fallbacks.length) return;

    // Always refresh fallback candidates for the current image source.
    img.dataset.mwFallbackList = JSON.stringify(fallbacks);
    img.dataset.mwFallbackIndex = "0";

    if (img.dataset.mwFallbackWired === "1") return;
    img.dataset.mwFallbackWired = "1";

    img.addEventListener("error", () => {
      let list = [];
      try { list = JSON.parse(img.dataset.mwFallbackList || "[]"); } catch (_) { list = []; }
      const idx = Number(img.dataset.mwFallbackIndex || "0");
      if (idx >= list.length) return;
      img.dataset.mwFallbackIndex = String(idx + 1);
      img.src = list[idx];
    });
  }

  // ---------------- Data normalization ----------------

  function normalizePurchased_(r) {
    // prefer your “purchased item name” column
    return String(
      r.purchased ||
      r.etsy_title ||
      r.item_title ||
      r.product_title ||
      r.product ||
      ""
    ).trim();
  }

  function normalizeFromRows(rows) {
    if (!rows.length) return [];
    const headers = rows[0].map(h => (h || "").trim().toLowerCase());
    const data = [];

    function findHeader(names) {
      for (const n of names) {
        const idx = headers.indexOf(n);
        if (idx !== -1) return idx;
      }
      return -1;
    }

    const iRating = findHeader(["rating", "stars", "review_rating"]);
    const iBody   = findHeader(["body", "review", "review_text", "comment", "content"]);
    const iAuthor = findHeader(["author", "name", "reviewer", "customer_name"]);
    const iDate   = findHeader(["date", "created_at", "created", "review_date"]);

    // NEW: purchased model/title (your sheet stores this in etsy_title)
    const iPurchased = findHeader(["etsy_title", "purchased", "item_title", "product_title", "product", "model", "vehicle"]);

    const photoCols = headers
      .map((h, i) => ({ h, i }))
      .filter(x => /(photo|image|pic|picture)/.test(x.h))
      .map(x => x.i);

    function extractUrls(raw) {
      const s = String(raw || "").trim();
      if (!s) return [];
      if (s.startsWith("[") && s.endsWith("]")) {
        try {
          const arr = JSON.parse(s);
          if (Array.isArray(arr)) return arr.map(u => String(u).trim()).filter(Boolean);
        } catch (_) {}
      }
      const matches = s.match(/https?:\/\/[^\s"'<>]+/g) || [];
      return matches.map(u => u.replace(/[),\]"']+$/g, ""));
    }

    function uniq(arr) {
      return Array.from(new Set(arr.filter(Boolean)));
    }

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      if (!row || row.every(x => !String(x || "").trim())) continue;

      const rating = iRating >= 0 ? Number(row[iRating]) : 0;
      const body = iBody >= 0 ? String(row[iBody] || "") : "";
      const author = iAuthor >= 0 ? String(row[iAuthor] || "") : "Customer";
      const created_at = iDate >= 0 ? String(row[iDate] || "") : "";

      const purchased = iPurchased >= 0 ? String(row[iPurchased] || "").trim() : "";

      let photos = [];
      for (const idx of photoCols) photos = photos.concat(extractUrls(row[idx]));
      if (!photos.length) for (const cell of row) photos = photos.concat(extractUrls(cell));

      photos = uniq(photos.map(normalizePhotoUrl)).filter(u =>
        /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(u) || u.includes("etsystatic.com/")
      );

      data.push({
        rating: Number.isFinite(rating) ? rating : 0,
        body,
        author: author || "Customer",
        created_at,
        photos,
        purchased
      });
    }

    return data;
  }

  function computeSummary(reviews) {
    const rated = reviews.filter(r => Number(r.rating) > 0);
    const count = rated.length;
    const sum = rated.reduce((a, r) => a + Number(r.rating || 0), 0);
    const avg = count ? sum / count : 0;
    return { count, avg };
  }

  function ensureLightbox() {
    let el = document.querySelector(".mw-lightbox");
    if (el) return el;
    el = document.createElement("div");
    el.className = "mw-lightbox";
    el.innerHTML = `
      <div class="mw-lightbox-stage">
        <button class="mw-lightbox-close" type="button" aria-label="Close image">×</button>
        <img alt="Review photo">
      </div>
    `;
    el.addEventListener("click", (e) => {
      // Close only when clicking backdrop, not the image itself.
      if (e.target === el) el.classList.remove("open");
    });
    const closeBtn = el.querySelector(".mw-lightbox-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        el.classList.remove("open");
      });
    }
    document.body.appendChild(el);
    return el;
  }

  function openLightbox(src) {
    const lb = ensureLightbox();
    const stage = lb.querySelector(".mw-lightbox-stage");
    const prevImg = stage ? stage.querySelector("img") : lb.querySelector("img");
    const img = document.createElement("img");
    img.alt = "Review photo";
    img.className = "mw-lightbox-img";
    img.addEventListener("load", () => {
      const maxW = Math.min(980, Math.floor(window.innerWidth * 0.95));
      const maxH = Math.floor(window.innerHeight * 0.90);
      const nw = Math.max(1, Number(img.naturalWidth) || 1);
      const nh = Math.max(1, Number(img.naturalHeight) || 1);
      const scale = Math.min(maxW / nw, maxH / nh);

      img.style.width = `${Math.round(nw * scale)}px`;
      img.style.height = `${Math.round(nh * scale)}px`;
      img.classList.add("is-ready");
    }, { once: true });
    img.addEventListener("error", () => img.classList.add("is-ready"), { once: true });
    if (prevImg) prevImg.replaceWith(img);
    else if (stage) stage.appendChild(img);
    else lb.appendChild(img);

    if (isDriveLikeUrl(src)) {
      applyDriveImgAttrs(img);
      installDriveFallback(img, src);
    } else {
      img.removeAttribute("referrerpolicy");
      img.removeAttribute("crossorigin");
    }

    img.src = src;

    lb.classList.add("open");
  }

  function renderBadge(avg, count) {
    return `
      ${starsHTML(avg)}
      <span class="mw-rating-text">${avg.toFixed(2)}</span>
      <span class="mw-rating-count">(${count})</span>
    `;
  }

  function renderReviewCard(r) {
    const photos = Array.isArray(r.photos) ? r.photos : [];
    const photoHTML = photos.slice(0, 6).map(u => {
      const eu = escapeHTML(u);
      const driveAttrs = isDriveLikeUrl(u) ? ` referrerpolicy="no-referrer" crossorigin="anonymous"` : "";
      return `<img src="${eu}" alt="Review photo" loading="lazy"${driveAttrs} data-mw-photo="${eu}">`;
    }).join("");

    const author = String(r.author || "Customer").trim() || "Customer";
    const purchased = normalizePurchased_(r);

    return `
      <article class="mw-review">
        <div class="mw-review-top">
          <div>
            <div class="mw-review-author">
              <span class="mw-author-name">${escapeHTML(author)}</span>
              ${purchased ? `<span class="mw-purchased">Purchased: ${escapeHTML(purchased)}</span>` : ``}
            </div>
            <div>${starsHTML(Number(r.rating || 0))}</div>
          </div>
          <div class="mw-review-date">${escapeHTML(fmtDate(r.created_at))}</div>
        </div>
        <p class="mw-review-body">${escapeHTML(r.body || "")}</p>
        ${photoHTML ? `<div class="mw-review-photos">${photoHTML}</div>` : ""}
      </article>
    `;
  }

  function safeTime_(v) {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  }

  function safeRating_(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  function buildTopPhotoUrls_(photoReviews, photoLimit) {
    const seen = new Set();
    const out = [];
    for (const r of photoReviews) {
      const photos = Array.isArray(r.photos) ? r.photos : [];
      for (const u of photos) {
        if (!u || seen.has(u)) continue;
        seen.add(u);
        out.push(u);
        if (out.length >= photoLimit) return out;
      }
    }
    return out;
  }

  function isMobile_() {
    return window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
  }

  function renderTopPhotos_(panel, photoUrls) {
    const grid = panel.querySelector("[data-mw-grid]");
    const carousel = panel.querySelector("[data-mw-carousel]");
    const track = panel.querySelector("[data-mw-track]");
    const prevBtn = panel.querySelector("[data-mw-prev]");
    const nextBtn = panel.querySelector("[data-mw-next]");

    const threshold = isMobile_() ? CAROUSEL_THRESHOLD_MOBILE : CAROUSEL_THRESHOLD_DESKTOP;
    const useCarousel = photoUrls.length > threshold;

    grid.innerHTML = "";
    track.innerHTML = "";

    if (!photoUrls.length) {
      grid.style.display = "none";
      carousel.style.display = "none";
      return;
    }

    if (!useCarousel) {
      carousel.style.display = "none";
      grid.style.display = "grid";

      for (const u0 of photoUrls) {
        const u = normalizePhotoUrl(u0);
        const img = document.createElement("img");
        img.src = u;
        img.alt = "Customer photo";
        img.loading = "lazy";

        if (isDriveLikeUrl(u)) {
          applyDriveImgAttrs(img);
          installDriveFallback(img, u);
        }

        img.addEventListener("click", () => openLightbox(u));
        grid.appendChild(img);
      }
      return;
    }

    grid.style.display = "none";
    carousel.style.display = "block";

    for (const u0 of photoUrls) {
      const u = normalizePhotoUrl(u0);
      const img = document.createElement("img");
      img.src = u;
      img.alt = "Customer photo";
      img.loading = "lazy";

      if (isDriveLikeUrl(u)) {
        applyDriveImgAttrs(img);
        installDriveFallback(img, u);
      }

      img.addEventListener("click", () => openLightbox(u));
      track.appendChild(img);
    }

    function updateNav() {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const x = track.scrollLeft;
      prevBtn.disabled = x <= 2;
      nextBtn.disabled = x >= (maxScroll - 2);
    }

    prevBtn.onclick = () => track.scrollBy({ left: -track.clientWidth, behavior: "smooth" });
    nextBtn.onclick = () => track.scrollBy({ left: track.clientWidth, behavior: "smooth" });

    track.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", updateNav);

    track.scrollLeft = 0;
    updateNav();
  }

  function sortReviews_(reviews, mode) {
    const arr = [...reviews];

    if (mode === "rating_desc") {
      arr.sort((a, b) => {
        const rd = safeRating_(b.rating) - safeRating_(a.rating);
        if (rd !== 0) return rd;
        return safeTime_(b.created_at) - safeTime_(a.created_at);
      });
      return arr;
    }

    if (mode === "rating_asc") {
      arr.sort((a, b) => {
        const rd = safeRating_(a.rating) - safeRating_(b.rating);
        if (rd !== 0) return rd;
        return safeTime_(b.created_at) - safeTime_(a.created_at);
      });
      return arr;
    }

    arr.sort((a, b) => safeTime_(b.created_at) - safeTime_(a.created_at));
    return arr;
  }

  async function initPanel(panel) {
    const dataUrl = panel.getAttribute("data-mw-data-url");
    const pageSize = Number(panel.getAttribute("data-mw-page-size") || 20);
    const photoLimit = Number(panel.getAttribute("data-mw-photo-limit") || 12);

    const csvText = await fetchText(dataUrl);

    let rawReviews;
    if (dataUrl.toLowerCase().includes(".json")) rawReviews = JSON.parse(csvText);
    else rawReviews = normalizeFromRows(parseCSV(csvText));

    // Normalize purchased field for JSON feeds too
    rawReviews.forEach((r) => {
      r.purchased = normalizePurchased_(r);
    });

    rawReviews.forEach((r, idx) => {
      if (!r.__mwKey) {
        const body = String(r.body || "");
        r.__mwKey = `${idx}|${r.created_at || ""}|${r.author || ""}|${safeRating_(r.rating)}|${body.slice(0, 40)}`;
      }
    });

    const { avg, count } = computeSummary(rawReviews);

    document.querySelectorAll("[data-mw-rating-badge]").forEach(el => {
      el.classList.add("mw-rating-badge");
      el.innerHTML = renderBadge(avg, count);
    });

    panel.innerHTML = `
      <div class="mw-reviews-wrap">
        <div class="mw-reviews-head">
          <div>
            <h2 class="mw-reviews-title">Reviews</h2>
            <div class="mw-reviews-sub">${avg.toFixed(2)} average · ${count} total</div>
          </div>

          <div class="mw-sort">
            <label for="mwSort">Sort</label>
            <select id="mwSort" data-mw-sort>
              <option value="newest">Newest (photo-first)</option>
              <option value="rating_desc">Highest rating</option>
              <option value="rating_asc">Lowest rating</option>
            </select>
          </div>
        </div>

        <div class="mw-photo-carousel" data-mw-carousel style="display:none">
          <button class="mw-photo-nav mw-prev" type="button" data-mw-prev aria-label="Previous photos">‹</button>
          <div class="mw-photo-track" data-mw-track></div>
          <button class="mw-photo-nav mw-next" type="button" data-mw-next aria-label="Next photos">›</button>
        </div>

        <div class="mw-photo-grid" data-mw-grid></div>

        <div data-mw-featured>
          <h3 class="mw-featured-title"></h3>
          <div class="mw-featured-list" data-mw-featured-list></div>
        </div>

        <div class="mw-review-list" data-mw-list></div>
        <button class="mw-more" type="button" data-mw-more>Load more</button>
      </div>
    `;

    const sortSelect = panel.querySelector("[data-mw-sort]");
    const featuredWrap = panel.querySelector("[data-mw-featured]");
    const featuredList = panel.querySelector("[data-mw-featured-list]");
    const list = panel.querySelector("[data-mw-list]");
    const more = panel.querySelector("[data-mw-more]");

    const newestByDate = sortReviews_(rawReviews, "newest");
    const photoReviewsNewest = newestByDate.filter(r => Array.isArray(r.photos) && r.photos.length);
    const topPhotos = buildTopPhotoUrls_(photoReviewsNewest, photoLimit);
    renderTopPhotos_(panel, topPhotos);

    let activeList = [];
    let shown = 0;

    function renderMode(mode) {
      list.innerHTML = "";
      shown = 0;
      more.style.display = "inline-block";

      if (mode === "newest") {
        const orderedNewest = newestByDate;

        const featured = photoReviewsNewest.slice(0, FEATURED_PHOTO_REVIEW_COUNT);
        const featuredKeys = new Set(featured.map(r => r.__mwKey));

        const rest = orderedNewest.filter(r => !featuredKeys.has(r.__mwKey));

        if (!featured.length) {
          featuredWrap.style.display = "none";
        } else {
          featuredWrap.style.display = "block";
          featuredList.innerHTML = featured.map(renderReviewCard).join("");
        }

        activeList = rest;
        return;
      }

      featuredWrap.style.display = "none";
      featuredList.innerHTML = "";

      activeList = sortReviews_(rawReviews, mode);
    }

    function loadMore() {
      const chunk = activeList.slice(shown, shown + pageSize);
      if (!chunk.length) { more.style.display = "none"; return; }
      list.insertAdjacentHTML("beforeend", chunk.map(renderReviewCard).join(""));
      shown += chunk.length;

      const imgs = panel.querySelectorAll("img[data-mw-photo]");
      for (const img of imgs) {
        const src = img.getAttribute("src") || "";
        if (isDriveLikeUrl(src)) installDriveFallback(img, src);
      }

      if (shown >= activeList.length) more.style.display = "none";
    }

    more.addEventListener("click", loadMore);

    sortSelect.addEventListener("change", () => {
      renderMode(sortSelect.value);
      loadMore();
    });

    panel.addEventListener("click", (e) => {
      const t = e.target;
      if (t && t.matches("[data-mw-photo]")) {
        const src = t.currentSrc || t.src || t.getAttribute("data-mw-photo") || "";
        if (src) openLightbox(src);
      }
    });

    renderMode("newest");
    loadMore();
  }

  document.addEventListener("DOMContentLoaded", async () => {
    const panels = document.querySelectorAll("[data-mw-shuttle-reviews-panel]");
    for (const p of panels) {
      try { await initPanel(p); }
      catch (e) { p.textContent = "Reviews failed to load."; }
    }
  });

})();
